import { Logger } from '@deqo/foundation';
import { useEffect, useMemo, useRef } from 'react';
import { telemetry } from '../../@vendor/Posthog';
import { Host } from '../../Host';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { ObjectManager } from '../../concepts/objects';
import { LocalStorageService, StorageService } from '../../platform/StorageService';
import { ActiveObject, resolve } from '../Container';
import { Sticky } from '../packages/paper/Sticky';
import { BoardInfo } from './BoardInfo';
import { EditorSession, useEditorSessionObserver } from './EditorSession';
import { MyRoleInfo } from './MyRoleInfo';
import { ParticipantManager } from './participants/ParticipantManager';

const ckey = (type: string) => `deqo/c/${type}`;
const counters = ['confetti', 'stickies'];
const debug = Logger.get('app.telemetry');

export class Telemetry extends ActiveObject {

    @resolve(ApiClient) private api!: ApiClient;
    @resolve(LocalStorageService) private localData!: StorageService;
    @resolve(ParticipantManager) private participants!: ParticipantManager;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(EditorSession) private session!: EditorSession;

    private hasEmitCrowd = false;

    public start() {
        this.emitBoardOpen();
        this.emitBoardCrowd();
        this.burden(
            () => this.flush(),
            this.session.observe(MyRoleInfo, (n, o) => {
                if (n.canParticipate !== o.canParticipate) {
                    this.emitBoardOpen();
                }
            }),
            this.participants.observe(this.emitBoardCrowd),
            this.participants.on('join', this.emitBoardCrowd),
            this.objects.createTrigger('Telemetry.OnCreateSticky')
                .for(Sticky).onCreated().do(x => this.increment('stickies', x.length)),
        );
        this.scheduler.timeout('flush', 0, () => this.flush());
    }

    public increment(type: 'confetti' | 'stickies', value: number) {
        this.localData.update(ckey(type), x => (Number(x) || 0) + value);
    }

    public async flush() {
        const body = [] as any[];
        for (const c of counters) {
            const count = this.localData.delete(ckey(c)) || 0;
            if (count) {
                body.push([c, count]);
            }
        }
        if (body.length) {
            await this.api.execs(v2.internal.notify(body));
            debug.log('Telm: flush');
            body.forEach(x => debug.log(JSON.stringify(x, null, 4)));
        }
        const after = (1000 * 60 * 5) + Math.round(Math.random() * 1000 * 60);
        this.scheduler.timeout('flush', after, () => this.flush());


    }

    private emitBoardOpen = () => {
        if (Host.offline) return;
        const { id: boardId } = this.session.get(BoardInfo);
        const { canParticipate, canManage } = this.session.get(MyRoleInfo);
        telemetry.emit('board.open', { boardId, canParticipate, canManage });
    };

    private emitBoardCrowd = () => {
        if (Host.offline) return;
        if (this.hasEmitCrowd) return;
        if (!this.session.get(MyRoleInfo).canParticipate) return;
        if (this.participants.online().length < 3) return;
        if (this.participants.getSelf()?.id === 'US000000ANON') return;
        const { id: boardId } = this.session.get(BoardInfo);
        telemetry.emit('board.crowd', { boardId });
        this.api.exec(v2.internal.notify([['crowd', boardId]]));
        this.hasEmitCrowd = true;
    };
}

export const useTrackedClickableRef = (eventName: string, eventData?: Record<string, any>) => {
    const ref = useRef<HTMLElement>(null);
    const board = useEditorSessionObserver(BoardInfo);

    const mergedData = useMemo(() => ({
        boardId: board.id,
        ...eventData,
    }), [board]);

    useEffect(() => {
        if (ref.current) {
            const onClick = (e: MouseEvent) => {
                if (e.target instanceof HTMLElement) {
                    telemetry.emit(eventName, mergedData);
                }
            };
            ref.current.addEventListener('click', onClick, { passive: true, capture: true });
            return () => ref.current?.removeEventListener('click', onClick);
        }
    }, [ref.current, eventName, mergedData]);

    // Style components making us use <any>!
    return { ref: ref as React.RefObject<any> };
};
